// HRMS_STADO/src/services/hrms.api.js
import axios from "axios";

let API_BASE = process.env.REACT_APP_API;

export const api = axios.create({
  baseURL: API_BASE,
});

// attach token + tenant on every request
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token");
  const tenant = localStorage.getItem("tenant");
  cfg.headers = cfg.headers || {};
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  cfg.headers["x-tenant-id"] = tenant;
  return cfg;
});

// ─── NEW: fire "session:expired" on 401 / expired token ───────────────────
let _expiredFired = false;
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const message = (err?.response?.data?.message || "").toLowerCase();
    const isExpired =
      status === 401 ||
      message.includes("expired") ||
      message.includes("invalid token") ||
      message.includes("invalid/expired");

    if (isExpired && !_expiredFired) {
      _expiredFired = true;
      window.dispatchEvent(new Event("session:expired"));
      setTimeout(() => { _expiredFired = false; }, 10_000);
    }
    return Promise.reject(err);
  }
);

// tiny helper to unwrap .data consistently
const ok = (p) =>
  p
    .then((r) => ({ data: r.data?.data ?? r.data }))
    .catch((e) => {
      const msg = e?.response?.data?.message || e.message || "Request failed";
      return Promise.reject(new Error(msg));
    });

/* ---------------- USERS / ME ---------------- */

export async function getMyProfile() {
  const { data } = await ok(api.get("/me"));
  return data?.user || data;
}

export async function updateMyProfile(payload, opts = {}) {
  // If you pass FormData (or opts.multipart=true), don't set Content-Type; axios will add boundary.
  const isFD = typeof FormData !== "undefined" && payload instanceof FormData;
  const cfg = {};
  if (opts.multipart || isFD) cfg.headers = {};

  const { data } = await ok(api.put("/me", payload, cfg));
  return data?.user || data;
}

export async function changeMyPassword(body) {
  const { data } = await ok(api.post("/me/change-password", body));
  return data;
}

/* ---------------- LEAVES ---------------- */

export function getLeaves(params = {}) {
  return ok(api.get("/leaves", { params }));
}

export function createLeave(payload) {
  // Accepts { employee?, type, from, to, reason } from UI,
  // but backend needs fromDate/toDate.
  const { from, to, ...rest } = payload || {};
  return ok(
    api.post("/leaves", {
      ...rest,
      fromDate: from, // map UI -> API
      toDate: to, // map UI -> API
    })
  );
}

export function updateLeaveStatus(id, status, body = {}) {
  const s = String(status || "").toLowerCase();
  if (!["approved", "rejected", "cancelled", "canceled"].includes(s)) {
    return Promise.reject(
      new Error("Status must be Approved, Rejected or Cancelled")
    );
  }

  if (s === "cancelled" || s === "canceled") {
    return ok(api.patch(`/leaves/${id}/cancel`, body));
  }
  const serverStatus = s === "approved" ? "Approved" : "Rejected";
  return ok(
    api.patch(`/leaves/${id}/review`, { status: serverStatus, ...body })
  );
}

export function getMyLeaves(params = {}) {
  return ok(api.get("/leaves", { params }));
}

/* ---------------- HOLIDAYS ---------------- */

export function getHolidays(year) {
  const params = year ? { year } : undefined;
  return ok(api.get("/holidays", { params }));
}

export function getHoliday(id) {
  return ok(api.get(`/holidays/${id}`));
}

export function getHolidayOptions() {
  return ok(api.get("/holidays/options"));
}

export function createHoliday(payload) {
  return ok(api.post("/holidays", payload));
}

export function deleteHoliday(id) {
  return ok(api.delete(`/holidays/${id}`));
}

export function updateHoliday(id, payload) {
  return ok(api.put(`/holidays/${id}`, payload));
}

export function getWeeklyOffs() {
  return ok(api.get("/hrms-settings/weekly-offs"));
}

export function updateWeeklyOffs(weeklyOffDays) {
  return ok(api.put("/hrms-settings/weekly-offs", { weeklyOffDays }));
}

/* ---------------- OFFICES ---------------- */

export function getOffices() {
  return ok(api.get("/offices"));
}

export function getOffice(id) {
  return ok(api.get(`/offices/${id}`));
}

export function getOfficeByTenant(tenantId) {
  return ok(api.get(`/offices/by-tenant/${tenantId}`));
}

export function createOffice(payload) {
  return ok(api.post("/offices", payload));
}

export function updateOffice(id, patch) {
  return ok(api.put(`/offices/${id}`, patch));
}

export function deleteOffice(id) {
  return ok(api.delete(`/offices/${id}`));
}


/* ---------------- SHIFTS ---------------- */
export function getShiftsByOffice(officeId) {
  return ok(api.get(`/shifts`, { params: { officeId } }));
}

export function createShift(payload) {
  return ok(api.post(`/shifts`, payload));
}

export function updateShift(id, payload) {
  return ok(api.put(`/shifts/${id}`, payload));
}

export function deleteShift(id) {
  return ok(api.delete(`/shifts/${id}`));
}

export function getEmployeeCurrentShift(employeeId) {
  return ok(api.get(`/employees/${employeeId}/current-shift`));
}

export function updateEmployeeShift(employeeId, payload) {
  return ok(api.put(`/employees/${employeeId}/shift`, payload));
}


/* ---------------- EMPLOYEES ---------------- */

export function getEmployees() {
  return ok(api.get("/employees"));
}

export function getEmployeeById(id) {
  return ok(api.get(`/employees/${id}`));
}

export function createEmployee(payload) {
  return ok(api.post("/employees", payload));
}

export async function checkEmployeeIdExists(employeeId) {
  const id = String(employeeId || "").trim();
  if (!id) return { exists: false };

  const { data } = await ok(
    api.get("/employees/exists", { params: { employeeId: id } })
  );

  return { exists: !!data?.exists };
}

export function updateEmployee(id, payload) {
  return ok(api.put(`/employees/${id}`, payload));
}

export function deleteEmployee(id) {
  return ok(api.delete(`/employees/${id}`));
}

export function importEmployees(file) {
  if (!file) {
    return Promise.reject(new Error("No file selected"));
  }

  const formData = new FormData();
  formData.append("file", file);

  return ok(
    api.post("/employees/import", formData, {
      headers: {
        // Let browser set boundary; this is optional but explicit
        "Content-Type": "multipart/form-data",
      },
    })
  );
}


/* ---------------- EMPLOYEE WEEKLY OFFS ---------------- */

// GET  /api/employees/weekly-offs/all
// Returns: [{ employeeId, weeklyOffDays: [1] }, ...]
export function getAllEmployeeWeeklyOffs() {
  return ok(api.get("/employees/weekly-offs/all"));
}

// GET  /api/employees/:id/weekly-offs
// Returns: { weeklyOffDays: [0, 6] }
export function getEmployeeWeeklyOffs(employeeId) {
  return ok(api.get(`/employees/${employeeId}/weekly-offs`));
}

// PUT  /api/employees/:id/weekly-offs
// Body:    { weeklyOffDays: [1] }
// Returns: { weeklyOffDays: [1] }
export function updateEmployeeWeeklyOffs(employeeId, weeklyOffDays) {
  return ok(api.put(`/employees/${employeeId}/weekly-offs`, { weeklyOffDays }));
}

/* ---------------- ATTENDANCE ---------------- */

export function getAttendance(params = {}) {
  return api.get("/attendances", { params });
}

export const getAttendanceByEmployee = (employeeId, params = {}) => {
  return api.get("/attendances", {
    params: { employee: employeeId, ...params },
  });
};

export function punchIn(id) {
  return api.post(`/attendances/${id}/punch-in`);
}

export function punchOut(id) {
  return api.post(`/attendances/${id}/punch-out`);
}

/* ---------------- GPS ---------------- */

export function getLiveLocations(params = {}) {
  const since =
    params.since || new Date(Date.now() - 15 * 60 * 1000).toISOString();
  return ok(api.get("/gps", { params: { ...params, since } }));
}

export function getLatestGps(params = {}) {
  return ok(api.get("/gps/latest", { params }));
}

export function postGpsPoint(payload, { resolveAddress = false } = {}) {
  return ok(api.post(`/gps?resolveAddress=${resolveAddress}`, payload));
}

/* ---------------- TODOS ---------------- */

export function getTodos(params = {}) {
  return ok(api.get("/todos", { params }));
}

export function createTodo(payload) {
  const body = {
    title: payload?.title,
    done: !!payload?.done,
  };
  return ok(api.post("/todos", body));
}

export function updateTodo(id, payload) {
  const body = {};
  if (payload?.title !== undefined) body.title = payload.title;
  if (payload?.done !== undefined) body.done = !!payload.done;
  if (payload?.order !== undefined) body.order = payload.order;

  return ok(api.patch(`/todos/${id}`, body));
}

export function deleteTodo(id) {
  return ok(api.delete(`/todos/${id}`));
}

/* ---------------- PAYROLL / SALARY ---------------- */

export function generatePayrollForMonth(year, month) {
  return ok(
    api.post("/payroll/generate", {
      year,
      month,
    })
  );
}

export function listPayrollByMonth(year, month) {
  return ok(
    api.get("/payroll", {
      params: { year, month },
    })
  );
}

export function getEmployeePayslip(employeeId, year, month) {
  return ok(
    api.get(`/payroll/${employeeId}`, {
      params: { year, month },
    })
  );
}

/* ---------------- REGULARIZATION ---------------- */

export function getRegularizations(params = {}) {
  return ok(api.get("/regularizations", { params }));
}

export function getRegularization(id) {
  return ok(api.get(`/regularizations/${id}`));
}

export function approveRegularization(id, remarks = "") {
  return ok(api.post(`/regularizations/${id}/approve`, { remarks }));
}

export function rejectRegularization(id, remarks) {
  return ok(api.post(`/regularizations/${id}/reject`, { remarks }));
}

// admin adds/fixes a day directly for an employee (applied immediately)
export function createRegularization(payload) {
  return ok(api.post("/regularizations", payload));
}